import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';

import { IntercomConfig } from '@libs/src/intercom/intercom-config';

declare let window: any;

interface IntercomData {
  email?: string;
  hide_default_launcher?: boolean;
  name?: string;
  user_id?: string;
  user_hash?: string;
  [propName: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  private userHash = new BehaviorSubject('');

  constructor(
    @Inject(IntercomConfig) private config: IntercomConfig,
    @Inject(PLATFORM_ID) protected platformId: object,
    @Optional() @Inject(Router) private router: Router,
  ) {
    if (!this.isBrowser()) {
      return;
    }

    this.loadIntercom(this.config);
    const routerEvents = this.router && this.router.events;
    if (routerEvents) {
      routerEvents
        .pipe(filter((event: Event) => event instanceof NavigationEnd))
        .subscribe(() => this.update());
    }
  }

  private insertToDOM(): void {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${this.config.appId}`;
    const scriptElement = document.getElementsByTagName('script')[0];
    scriptElement.parentNode.insertBefore(script, scriptElement);
  }

  private isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  loadIntercom(config: IntercomConfig): void {
    const ic = window.Intercom;
    if ('function' === typeof ic) {
      ic('reattach_activator');
      ic('update', config);
    } else {
      // eslint-disable-next-line func-style
      const i: any = function () {
        // eslint-disable-next-line prefer-rest-params
        i.c(arguments);
      };
      i.q = [];
      i.c = function (args: any) {
        i.q.push(args);
      };
      window.Intercom = i;
      if (window.attachEvent) {
        window.attachEvent('onload', () => this.insertToDOM());
      } else {
        window.addEventListener('load', () => this.insertToDOM(), false);
      }
    }
  }

  boot(intercomData?: IntercomData): void {
    if (!this.isBrowser()) {
      return;
    }
    const data = {
      ...intercomData,
      app_id: this.config.appId,
      hide_default_launcher: true,
    };
    return window.Intercom('boot', data);
  }

  setUserHash(userHash: string) {
    this.userHash.next(userHash);
  }

  checkIntercomStatus() {
    if (!this.isBrowser()) {
      return;
    }
    return !!window.Intercom && window.Intercom.booted;
  }

  shutdown(): void {
    if (!this.isBrowser()) {
      return;
    }
    return window.Intercom('shutdown');
  }

  update(data?: IntercomData): void {
    if (!this.isBrowser()) {
      return;
    }
    return window.Intercom('update', data);
  }

  updateUserProperties(update: IntercomData) {
    return this.isUserIdentified().pipe(
      take(1),
      tap((userHash: string) => {
        return this.update({
          ...update,
          ...(userHash && { user_hash: userHash }),
        });
      }),
    );
  }

  toggleLauncher(isHidden: boolean) {
    this.update({ hide_default_launcher: isHidden });
  }

  private isUserIdentified() {
    return this.userHash.pipe(filter((userHash: string) => !!userHash));
  }

  showNewMessage(prePopulatedMessage?: string) {
    return window.Intercom('showNewMessage', prePopulatedMessage);
  }

  /**
   * @param spaceName - From https://developers.intercom.com/installing-intercom/docs/intercom-javascript#intercomshowspace-spacename
   * */
  showSpace(spaceName: 'home' | 'messages' | 'help' | 'news' | 'tasks') {
    return window.Intercom('showSpace', spaceName);
  }

  hide() {
    return window.Intercom('hide');
  }
}
